import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { RegistroService } from '@app/modules/pagamento/folha-residente/service/registro.service'
import {
  OcorrenciaModel,
  ProcessamentoOcorrenciaModel,
  TipoContestacaoEnum,
  TipoOcorrenciaEnum,
} from '@shared/models/backend'
import moment from 'moment'

const DATE_ISO_FORMAT = 'YYYY-MM-DD'
const PTBR_DATE_FORMAT = 'DD/MM/YYYY'

@Component({
  selector: 'app-registro-card',
  templateUrl: './registro-card.component.html',
  styleUrls: ['./registro-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistroCardComponent {
  @Input()
  canDelete = false

  @Input()
  registro: ProcessamentoOcorrenciaModel

  @Input()
  isUserResidente = false

  @Output()
  deleteClicked: EventEmitter<ProcessamentoOcorrenciaModel> = new EventEmitter()

  constructor(private registroService: RegistroService, private datePipe: DatePipe) {}

  onDeleteClicked() {
    this.deleteClicked?.emit(this.registro)
  }

  getRegistroIcon() {
    switch (this.registro?.ocorrencia?.tipoOcorrencia?.id) {
      case TipoOcorrenciaEnum.FALTA:
      case TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA:
        return 'event'
      case TipoOcorrenciaEnum.COMPENSACAO_FALTA:
      case TipoOcorrenciaEnum.COMPENSACAO_PRORROGACAO_LICENCA_MATERNIDADE:
      case TipoOcorrenciaEnum.ACERTO_BOLSA:
        return 'local_atm'
      case TipoOcorrenciaEnum.FALTA_MUDANCA_SITUACAO:
        return 'pending_actions'
      case TipoOcorrenciaEnum.SUSPENSO_MUDANCA_SITUACAO:
        return 'person_off'
    }
  }

  get titleRef() {
    return `Ref. ${this.registroService.getRegistroRefText(this.registro)}`
  }

  get title() {
    return this.registroService.getTitle(this.registro)
  }

  getPeriodoValue(ocorrencia: OcorrenciaModel) {
    if (!ocorrencia?.dataInicio || !ocorrencia?.dataFim) {
      return null
    }

    const dataInicioResidencia = this.getDataInicioResidencia(ocorrencia)

    if (dataInicioResidencia) {
      return dataInicioResidencia.format(PTBR_DATE_FORMAT)
    }

    const formattedFrom = this.getMoment(ocorrencia?.dataInicio)?.format(PTBR_DATE_FORMAT)

    const formattedTo = this.getMoment(ocorrencia?.dataFim)?.format(PTBR_DATE_FORMAT)

    if (this.isPeriodo(ocorrencia) && formattedFrom !== formattedTo) {
      return `${formattedFrom} a ${formattedTo} (${ocorrencia?.numeroDias} dias)`
    }

    return formattedFrom
  }

  private getMoment(isoDate: string) {
    const date = moment(isoDate, DATE_ISO_FORMAT)
    return date.isValid() ? date : null
  }

  getDataInicioResidencia(ocorrencia: OcorrenciaModel) {
    const tipoOcorrencia = ocorrencia?.tipoOcorrencia?.id

    if (tipoOcorrencia === TipoOcorrenciaEnum.ACERTO_BOLSA) {
      return this.getMoment(ocorrencia?.dataInicio)
    } else if (tipoOcorrencia === TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA) {
      return this.getMoment(ocorrencia?.dataFim).add(1, 'days')
    }

    return null
  }

  getPeriodoTitle(ocorrencia: OcorrenciaModel) {
    const needUseDataInicioResidencia = this.needUseDataInicioResidencia(ocorrencia)
    const prefix = this.isPeriodo(ocorrencia) && !needUseDataInicioResidencia ? 'Período' : 'Data'
    const suffix = ' ' + (needUseDataInicioResidencia ? 'de início da residência' : 'da ocorrência')
    return prefix + suffix
  }

  needUseDataInicioResidencia(ocorrencia: OcorrenciaModel) {
    return [TipoOcorrenciaEnum.ACERTO_BOLSA, TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA].includes(
      ocorrencia?.tipoOcorrencia?.id
    )
  }

  isPeriodo(ocorrencia: OcorrenciaModel) {
    return ocorrencia?.numeroDias > 1 && ocorrencia.tipoOcorrencia?.id !== TipoOcorrenciaEnum.SUSPENSO_MUDANCA_SITUACAO
  }

  get ocorrenciaRelacionadaDesc() {
    const relacionada = this.registro?.ocorrenciaRelacionada
    if (relacionada) {
      const periodoText = this.getPeriodoValue(relacionada)
      const refText = this.registroService.getOcorrenciaRefText(relacionada)
      return `${periodoText} Ref. ${refText}`
    }
    return null
  }

  get diasDesc() {
    switch (this.registro?.ocorrencia?.tipoOcorrencia?.id) {
      case TipoOcorrenciaEnum.FALTA:
      case TipoOcorrenciaEnum.FALTA_MUDANCA_SITUACAO:
      case TipoOcorrenciaEnum.SUSPENSO_MUDANCA_SITUACAO:
      case TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA:
        return 'Dias descontados'
      case TipoOcorrenciaEnum.COMPENSACAO_FALTA:
      case TipoOcorrenciaEnum.COMPENSACAO_PRORROGACAO_LICENCA_MATERNIDADE:
        return 'Dias compensados'
      case TipoOcorrenciaEnum.ACERTO_BOLSA:
        return 'Dias acertados'
    }

    return null
  }

  get tipoOcorrenciaId() {
    return this.registro?.ocorrencia?.tipoOcorrencia?.id
  }

  get diasIconTooltipText() {
    if (this.isUserResidente) {
      switch (this.registro?.ocorrencia?.tipoOcorrencia?.id) {
        case TipoOcorrenciaEnum.SUSPENSO_MUDANCA_SITUACAO:
        case TipoOcorrenciaEnum.FALTA_MUDANCA_SITUACAO:
          if (!this.registro?.ocorrencia?.dataFim) {
            return 'Você não será incluído nas próximas competências.'
          }
          break
        case TipoOcorrenciaEnum.ACERTO_BOLSA:
          return 'Referente aos dias das competências anteriores que sua transferência estava concluída e sua bolsa não foi paga.'
        case TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA:
          return 'Sua residência foi iniciada no meio da competência atual, desta forma os dias que antecederam a sua matrícula não são pagos.'
      }
    } else {
      if (this.tipoOcorrenciaId === TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA) {
        return 'O residente iniciou a residência no meio da competência atual, desta forma os dias que antecederam a sua matrícula não serão pagos.'
      }

      if (this.tipoOcorrenciaId === TipoOcorrenciaEnum.SUSPENSO_MUDANCA_SITUACAO) {
        return 'O residente não será incluído nas próximas competências.'
      }

      if (this.tipoOcorrenciaId === TipoOcorrenciaEnum.COMPENSACAO_PRORROGACAO_LICENCA_MATERNIDADE) {
        return 'Residentes que tiveram prorrogação de licença maternidade durante suas residências devem compensar esse período após entrarem em integralização.'
      }
    }

    if (!this.registro?.concluido) {
      return 'Os demais dias serão descontados na próxima folha.'
    }

    return null
  }

  get diasValue() {
    return this.registro?.numeroDias + ' dias'
  }

  get isAlteracaoSituacao() {
    return [TipoOcorrenciaEnum.FALTA_MUDANCA_SITUACAO, TipoOcorrenciaEnum.SUSPENSO_MUDANCA_SITUACAO].includes(
      this.tipoOcorrenciaId
    )
  }

  get isRegistradoAutomaticamente() {
    return [TipoOcorrenciaEnum.PERIODO_PRE_MATRICULA, TipoOcorrenciaEnum.ACERTO_BOLSA].includes(this.tipoOcorrenciaId)
  }

  get descricaoOcorrenciaRelacionada() {
    if (this.registro.tipoContestacao === TipoContestacaoEnum.FALTA_REGISTRADA_INCORRETAMENTE) {
      const dataInicio = this.datePipe.transform(this.registro.dataInicio, 'dd/MM/yyyy')
      const dataFim = this.datePipe.transform(this.registro.dataFim, 'dd/MM/yyyy')
      return `${this.registro.ocorrencia.tipoOcorrencia.descricao} - ref ${this.registro.ocorrenciaRelacionada.referencia} - ${dataInicio} a ${dataFim}`
    }
    return null
  }

  get canShowDataRegistro() {
    return TipoOcorrenciaEnum.COMPENSACAO_PRORROGACAO_LICENCA_MATERNIDADE !== this.tipoOcorrenciaId
  }
}
